// src/components/RequireActiveSubscription.jsx
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthService } from "../services/auth";
import { getRolesStatuses } from "../services/subscription";

export default function RequireActiveSubscription({ children }) {
  const nav = useNavigate();
  const loc = useLocation();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [rolesStatuses, setRolesStatuses] = useState(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        
        // Vérifier le cache global d'abord
        const userCache = typeof window !== 'undefined' ? window.__userCache : null;
        const CACHE_DURATION = 30000;
        const now = Date.now();
        
        let userData = null;
        let statusesData = null;
        
        // Récupérer les données utilisateur depuis le cache ou l'API
        if (userCache?.data && (now - userCache.timestamp) < CACHE_DURATION) {
          userData = userCache.data;
        } else {
          userData = await AuthService.me().catch(() => null);
          if (userData && typeof window !== 'undefined') {
            if (!window.__userCache) window.__userCache = {};
            window.__userCache.data = userData;
            window.__userCache.timestamp = now;
          }
        }

        if (!mounted) return;
        
        // Vérifier si l'utilisateur est connecté
        if (!userData) {
          nav("/connexion", { replace: true, state: { next: loc.pathname + loc.search } });
          return;
        }

        // Vérifier le rôle (owner ou vendor)
        if (!["owner", "vendor"].includes(userData.role)) {
          nav("/profil", { replace: true });
          return;
        }

        setMe(userData);

        // Récupérer les statuts des rôles depuis le cache ou l'API
        if (userCache?.rolesData && (now - userCache.rolesTimestamp) < CACHE_DURATION) {
          statusesData = userCache.rolesData;
        } else {
          statusesData = await getRolesStatuses().catch(() => null);
          if (statusesData && typeof window !== 'undefined') {
            if (!window.__userCache) window.__userCache = {};
            window.__userCache.rolesData = statusesData;
            window.__userCache.rolesTimestamp = now;
          }
        }

        if (!mounted) return;
        setRolesStatuses(statusesData);

        // Vérifier si l'abonnement est actif
        const currentRole = userData.role;
        const currentRoleStatus = statusesData?.statuses?.[currentRole];
        const isActive = currentRoleStatus?.status === "active";

        if (!isActive) {
          // Rediriger vers la page d'abonnements avec le rôle en paramètre
          nav(`/abonnements?role=${currentRole}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de l'abonnement:", error);
        nav("/connexion", { replace: true });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [nav, loc.pathname, loc.search]);

  if (loading) {
    return (
      <div className="min-h-dvh grid place-items-center bg-white">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Vérification de l'accès...</p>
        </div>
      </div>
    );
  }

  // Si on arrive ici, toutes les vérifications sont passées
  return children;
}

